import { useState, useEffect, useRef, useCallback } from "react";
import { getOrCreateDeviceId, getElectronDeviceId } from "../utils/device-id";

const isCWBrowser = () => !!window.electronAPI?.mesh;

function useMeshTransport(config) {
  const {
    bunitId,
    tillId,
    enabled = false,
    debug = false,
    onMessage: customOnMessage,
    onConnect: customOnConnect,
    onDisconnect: customOnDisconnect,
  } = config;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [orderQueue, setOrderQueue] = useState([]);
  const [visibleOrders, setVisibleOrders] = useState([]);
  const [orderUpdateFlag, setOrderUpdateFlag] = useState(0);
  const [deviceId] = useState(() => getOrCreateDeviceId());
  // The self-echo filter below must compare against the same id meshPlugin.js
  // stamps on outgoing envelopes — that's the Electron hardware-anchored id
  // (getDeviceId() in electron/device.js), NOT this POS-local `deviceId`
  // (a separate localStorage UUID used for KDS duplicate-event tracking).
  // Comparing against the wrong one meant a device's own broadcasts were
  // never filtered out and got reprocessed as if they came from a peer.
  const [electronDeviceId, setElectronDeviceId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getElectronDeviceId().then((id) => {
      if (!cancelled) setElectronDeviceId(id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const MAX_VISIBLE_NOTIFICATIONS = 1;

  const cleanupRef = useRef(null);
  const eventListenersRef = useRef({});
  const callbacksRef = useRef({ onMessage: null, onConnect: null, onDisconnect: null });
  const statusPollRef = useRef(null);
  const processedIdsRef = useRef(new Set());

  useEffect(() => {
    callbacksRef.current = {
      onMessage: customOnMessage,
      onConnect: customOnConnect,
      onDisconnect: customOnDisconnect,
    };
  }, [customOnMessage, customOnConnect, customOnDisconnect]);

  const log = useCallback(
    (...args) => {
      if (debug) console.debug("[MeshTransport]", ...args);
    },
    [debug]
  );

  const subscribe = useCallback((eventType, callback) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = new Set();
    }
    eventListenersRef.current[eventType].add(callback);
    return () => eventListenersRef.current[eventType]?.delete(callback);
  }, []);

  const emitEvent = useCallback((eventType, payload) => {
    const listeners = eventListenersRef.current[eventType];
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (err) {
          console.error("[MeshTransport] Event listener error:", err);
        }
      });
    }
  }, []);

  const normalizeEvent = useCallback((msg) => {
    const type = msg.type;
    switch (type) {
      case "order:new":
        return {
          event: "order:new",
          payload: {
            ...msg.payload,
            orderId: msg.payload?.orderId || msg.payload?.fbOrderId,
            externalId: msg.payload?.externalId || msg.payload?.fbOrderId,
          },
        };
      case "order:update":
        return {
          event: "order:update",
          payload: {
            ...msg.payload,
            orderId: msg.payload?.orderId || msg.payload?.fbOrderId,
          },
        };
      case "table:status":
        return {
          event: "TABLE_STATUS_UPDATE",
          payload: msg.payload,
        };
      case "kot:new":
        return {
          event: "NEW_KOT",
          payload: msg.payload,
        };
      case "kot:cancel":
        return {
          event: "KOT_CANCEL",
          payload: msg.payload,
        };
      case "cart:sync":
        return {
          event: "ORDER_UPSERT",
          payload: msg.payload,
        };
      default:
        return { event: type, payload: msg.payload };
    }
  }, []);

  const translateToWSFormat = useCallback(
    (msg) => {
      if (electronDeviceId && msg.deviceId === electronDeviceId) return null;

      const normalized = normalizeEvent(msg);
      if (!normalized) return null;

      const dedupKey = `${normalized.event}_${msg.timestamp || Date.now()}`;
      if (processedIdsRef.current.has(dedupKey)) return null;
      processedIdsRef.current.add(dedupKey);
      if (processedIdsRef.current.size > 500) {
        const entries = Array.from(processedIdsRef.current);
        processedIdsRef.current = new Set(entries.slice(-500));
      }

      return {
        type: normalized.event,
        data: normalized.payload,
        streamId: `mesh_${msg.timestamp || Date.now()}`,
        bunitId: bunitId,
        timestamp: msg.timestamp,
        source: "mesh",
      };
    },
    [electronDeviceId, bunitId, normalizeEvent]
  );

  const sendMessage = useCallback(
    (data) => {
      if (!isCWBrowser()) return false;
      const mesh = window.electronAPI.mesh;
      const type = data.type || data.event || "message";
      mesh.send(type, { ...data, deviceId, bunitId, tillId });
      log("Mesh sent:", type, data);
      return true;
    },
    [deviceId, bunitId, tillId, log]
  );

  const sendMeshEvent = useCallback(
    (type, payload) => {
      if (!isCWBrowser()) return false;
      window.electronAPI.mesh.send(type, { ...payload, deviceId, bunitId, tillId });
      log("Mesh event sent:", type);
      return true;
    },
    [deviceId, bunitId, tillId, log]
  );

  const removeVisibleOrder = useCallback((orderId) => {
    setVisibleOrders((prev) => prev.filter((order) => order?.data?.orderId !== orderId));
  }, []);

  const removeVisibleOrderByExternalId = useCallback((externalId) => {
    setVisibleOrders((prev) => prev.filter((order) => order?.data?.externalId !== externalId));
  }, []);

  useEffect(() => {
    if (visibleOrders.length < MAX_VISIBLE_NOTIFICATIONS && orderQueue.length > 0) {
      const nextOrder = orderQueue[0];
      setVisibleOrders((prev) => [...prev, nextOrder]);
      setOrderQueue((prev) => prev.slice(1));
    }
  }, [orderQueue, visibleOrders]);

  useEffect(() => {
    if (!enabled || !bunitId || !isCWBrowser()) return;

    const mesh = window.electronAPI.mesh;
    log("Initializing mesh transport for bunit:", bunitId);

    const handleMeshMessage = (msg) => {
      const wsFormatMsg = translateToWSFormat(msg);
      if (!wsFormatMsg) return;

      log("Mesh message received:", wsFormatMsg.type, wsFormatMsg);
      setLastMessage(wsFormatMsg);

      emitEvent(wsFormatMsg.type, wsFormatMsg.data);

      const normalized = normalizeEvent(msg);
      if (normalized.event === "order:new") {
        setOrderQueue((prev) => [...prev, { data: normalized.payload }]);
        setOrderUpdateFlag((prev) => prev + 1);
      }
      if (normalized.event === "order:update" || normalized.event === "ORDER_UPSERT") {
        setOrderUpdateFlag((prev) => prev + 1);
      }

      if (callbacksRef.current.onMessage) {
        callbacksRef.current.onMessage(wsFormatMsg);
      }
    };

    cleanupRef.current = mesh.onMessage(handleMeshMessage);

    const checkStatus = async () => {
      try {
        const status = await mesh.getStatus();
        const isConnected = status.mode === "host" || status.mode === "client";
        setConnected(isConnected);
        if (isConnected && !connected) {
          log("Mesh connected, mode:", status.mode);
          if (callbacksRef.current.onConnect) callbacksRef.current.onConnect();
        }
      } catch (err) {
        log("Status check error:", err);
      }
    };

    checkStatus();
    statusPollRef.current = setInterval(checkStatus, 5000);

    return () => {
      if (cleanupRef.current) cleanupRef.current();
      if (statusPollRef.current) clearInterval(statusPollRef.current);
    };
  }, [enabled, bunitId, connected, log, translateToWSFormat, emitEvent, normalizeEvent]);

  const connect = useCallback(() => {
    log("Mesh connect — managed by CW Browser shell");
  }, [log]);

  const disconnect = useCallback(() => {
    if (cleanupRef.current) cleanupRef.current();
    setConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    log("Mesh reconnect — re-checking status");
    if (isCWBrowser()) {
      window.electronAPI.mesh.getStatus().then((status) => {
        setConnected(status.mode === "host" || status.mode === "client");
      });
    }
  }, [log]);

  return {
    connected,
    reconnecting,
    lastMessage,
    deviceId,
    connect,
    disconnect,
    reconnect,
    sendMessage,
    sendMeshEvent,
    visibleOrders,
    orderUpdateFlag,
    subscribe,
    removeVisibleOrder,
    removeVisibleOrderByExternalId,
    isMesh: true,
  };
}

export { isCWBrowser };
export default useMeshTransport;
